import { ActionContext as VuexActionContext, Store } from 'vuex'
import { WizardStep } from '@/types/landlord-order.types'
import { RoutePath } from '@/router/enum'
import { Namespace } from '@/store/Namespaces'
/* istanbul ignore file */

export const namespace = (fragment: string) => `${Namespace.LandlordOrder}/${Namespace.LandlordOrderWizard}/${fragment}`

export enum Property {
  ActiveStep = 'activeStep',
  Steps = 'steps',
  InvalidSteps = 'InvalidSteps'
}

enum Setter {
  ActiveStep = 'setActiveStep',
  CompletedStep = 'setCompletedStep',
  SetStepValidity = 'setStepValidity',
  ResetSteps = 'resetSteps'
}

enum Action {
  UpdateActiveStep = 'updateActiveStep',
  UpdateCompletedStep = 'updateCompletedStep',
  UpdateIsStepValid = 'updateIsStepValid',
  ResetWizard = 'resetWizard',
}

interface State {
  [Property.ActiveStep]: number;
  [Property.Steps]: {
    [key: number]: WizardStep;
  };
  [Property.InvalidSteps]: [];
}

export type ActionContext = VuexActionContext<State, {}>

export const getActiveStep = (store: Store<{}>): number => store.getters[namespace(Property.ActiveStep)]
export const getSteps = (store: Store<{}>): { [key: number]: WizardStep } => store.getters[namespace(Property.Steps)]
export const getInvalidSteps = (store: Store<{}>): [] => store.getters[namespace(Property.InvalidSteps)]

export const setActiveStep = (store: Store<{}>, step: number): Promise<void> => store.dispatch(namespace(Action.UpdateActiveStep), step)
export const setCompletedStep = (store: Store<{}>, step: number): Promise<void> => store.dispatch(namespace(Action.UpdateCompletedStep), step)
export const setIsStepValid = (store: Store<State>, step: number, isValid: boolean): Promise<void> => store.dispatch(namespace(Action.UpdateIsStepValid), { step, isValid })

export const resetWizard = (store: Store<State>): Promise<void> => store.dispatch(namespace(Action.ResetWizard))

const initialStepsState = {
  1: {
    title: 'Organisation',
    route: RoutePath.LandlordOrderOrganisation,
    completed: true,
    isValid: true
  },
  2: {
    title: 'Tjänster',
    route: RoutePath.LandlordOrderServices,
    completed: false,
    isValid: true
  },
  3: {
    title: 'Adressinformation',
    route: RoutePath.LandlordOrderProperty,
    completed: false,
    isValid: true
  },
  4: {
    title: 'Kontakt och leverans',
    route: RoutePath.LandlordOrderContact,
    completed: false,
    isValid: true
  },
  5: {
    title: 'Sammanfattning och skicka',
    route: RoutePath.LandlordOrderSummary,
    completed: false,
    isValid: true
  }
}
const landlordWizardModule = {
  namespaced: true,
  state: {
    [Property.ActiveStep]: 1,
    [Property.Steps]: initialStepsState
  },
  getters: {
    [Property.ActiveStep]: (state: State) => state.activeStep,
    [Property.Steps]: (state: State) => state.steps,
    [Property.InvalidSteps]: (state: State) => {
      const invalidSteps = []
      for (const key in state.steps) {
        if (!state.steps[key].isValid) {
          invalidSteps.push(state.steps[key])
        }
      }
      return invalidSteps
    }
  },
  mutations: {
    [Setter.ActiveStep]: (state: State, step: number) => {
      state.activeStep = step
    },
    [Setter.CompletedStep]: (state: State, step: number) => {
      state.steps = Object.assign({
        ...state.steps,
        [step]: {
          ...state.steps[step],
          completed: true
        }
      })
    },
    [Setter.SetStepValidity]: (state: State, { step, isValid }: { step: number; isValid: boolean }) => {
      state.steps[step].isValid = isValid
    },
    [Setter.ResetSteps]: (state: State) => {
      state.steps = initialStepsState
    }
  },
  actions: {
    [Action.UpdateActiveStep]: ({ commit }: ActionContext, payload: number): void => {
      commit(Setter.ActiveStep, payload)
    },
    [Action.UpdateCompletedStep]: ({ commit }: ActionContext, payload: number): void => {
      const nextStep = payload + 1

      commit(Setter.CompletedStep, nextStep)
      commit(Setter.ActiveStep, nextStep)
    },
    [Action.UpdateIsStepValid]: ({ commit }: ActionContext, payload: { step: number; isValid: boolean }): void => {
      commit(Setter.SetStepValidity, payload)
    },
    [Action.ResetWizard]: ({ commit }: ActionContext): void => {
      commit(Setter.ActiveStep, 1)
      commit(Setter.ResetSteps)
    }
  }
}

export default landlordWizardModule
